import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaClipboardList,
  FaEnvelopeOpenText,
  FaKey,
  FaTools,
  FaUsersCog,
} from "react-icons/fa";
import { FaDatabase } from "react-icons/fa6";

import { useCan } from "../authz/useCan";
import {
  LdsOverviewCard,
  LdsOverviewGrid,
  LdsOverviewHero,
  LdsOverviewPage,
} from "../components/lds/LdsOverview";

const AdminOverview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManageUsers = useCan("admin.users", "manage");
  const canNotificationsManage = useCan("admin.notifications", "manage");
  const canNotificationsLegacy = useCan("admin.notifications", "send");
  const canManageBackupsPermission = useCan("admin.backups", "manage");
  const canViewLogs = useCan("admin.audit", "read");
  const canManageOutingInfo = useCan("outing.info", "manage");
  const canMailchimpStatus = useCan("newsletter.recipients", "status");
  const canMailchimpExport = useCan("newsletter.recipients", "export");
  const canMailchimpSync = useCan("newsletter.recipients", "sync");
  const canManageEmailValidation = useCan("email.validation", "manage");
  const canManageBackups = canManageBackupsPermission;
  const canSeeNotifications = canNotificationsManage || canNotificationsLegacy;

  const canMaintenance =
    canManageOutingInfo ||
    canMailchimpStatus ||
    (canMailchimpExport && canMailchimpSync) ||
    canManageEmailValidation;
  const canSeeLogs = canViewLogs;

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaTools}
        eyebrow={t("navigation.management", {
          defaultValue: "Beheer",
        })}
        title={t("admin.management.title")}
        description={t("admin.management.description", {
          defaultValue:
            "Beheer gebruikers, rollen, notificaties, back-ups en onderhoud.",
        })}
      />

      <LdsOverviewGrid>
        {canManageUsers && (
          <LdsOverviewCard
            icon={FaUsersCog}
            eyebrow={t("admin.management.users_category", {
              defaultValue: "Users",
            })}
            title={t("admin.users.title")}
            description={t("admin.users.description")}
            actionLabel={t("common.open", { defaultValue: "Open" })}
            onClick={() => navigate("/admin/users")}
            animationIndex={0}
          />
        )}

        {canManageUsers && (
          <LdsOverviewCard
            icon={FaKey}
            eyebrow={t("admin.management.roles_category", {
              defaultValue: "Roles",
            })}
            title={t("admin.roles.title")}
            description={t("admin.roles.description")}
            actionLabel={t("common.open", { defaultValue: "Open" })}
            onClick={() => navigate("/admin/roles")}
            animationIndex={1}
          />
        )}

        {canManageBackups && (
          <LdsOverviewCard
            icon={FaDatabase}
            eyebrow={t("admin.management.backups_category", {
              defaultValue: "Backups",
            })}
            title={t("admin.backups.title", { defaultValue: "Back-ups" })}
            description={t("admin.backups.description", {
              defaultValue: "Download en beheer database back-ups.",
            })}
            actionLabel={t("common.open", { defaultValue: "Open" })}
            onClick={() => navigate("/admin/backups")}
            animationIndex={2}
          />
        )}

        {canSeeNotifications && (
          <LdsOverviewCard
            icon={FaEnvelopeOpenText}
            eyebrow={t("admin.management.notifications_category", {
              defaultValue: "Notifications",
            })}
            title={t("admin.notifications.title", {
              defaultValue: "Notificaties",
            })}
            description={t("admin.notifications.description", {
              defaultValue: "Beheer e-mailnotificaties en verstuur testmails.",
            })}
            actionLabel={t("common.open", { defaultValue: "Open" })}
            onClick={() => navigate("/admin/notifications")}
            animationIndex={3}
          />
        )}

        {canSeeLogs && (
          <LdsOverviewCard
            icon={FaClipboardList}
            eyebrow={t("admin.management.logs_category", {
              defaultValue: "Logging",
            })}
            title={t("admin.logs.title", { defaultValue: "Logging" })}
            description={t("admin.logs.description", {
              defaultValue: "Bekijk systeem logging en filter op specifieke events.",
            })}
            actionLabel={t("common.open", { defaultValue: "Open" })}
            onClick={() => navigate("/admin/logs")}
            animationIndex={4}
          />
        )}

        {canMaintenance && (
          <LdsOverviewCard
            icon={FaTools}
            eyebrow={t("admin.management.maintenance_category", {
              defaultValue: "Maintenance",
            })}
            title={t("admin.maintenance.title", { defaultValue: "Maintenance" })}
            description={t("admin.maintenance.expire_desc", {
              defaultValue: "Onderhoudsacties voor uitjes en dossiers.",
            })}
            actionLabel={t("common.open", { defaultValue: "Open" })}
            onClick={() => navigate("/maintenance")}
            animationIndex={5}
          />
        )}
      </LdsOverviewGrid>
    </LdsOverviewPage>
  );
};

export default AdminOverview;
